"use client";

import type { SseFrame } from "@/shared/builder-session";
import { flowopsFetch } from "@/shared/builder-session";

/**
 * Browser client for FlowOps agent-runtime multiplex (`POST /api/agent-runtime/stream`).
 * Port of Ajai's `multiplex-client.ts`: JSON turns + long-lived hub, seq/since resume,
 * resync-before-reattach, rejected attach, idle watchdog, paced reconnect, and
 * manual `retryNow()`.
 *
 * Product events arrive as `event: mx` envelopes `{ sessionId, seq, event, data }`.
 */

type SessionListener = (frame: SseFrame) => void;

export type AgentRuntimeLifecycle = {
  onSubscribed?: (sessionId: string) => void;
  onSubscribeLost?: (sessionId: string, message: string) => void;
  /**
   * Gap too old / reconnect catch-up.
   * When `frameAlreadyDelivered`, callers already painted `resync-required` —
   * only schedule orphan resume (do not paint again).
   */
  onSubscribeResync?: (
    sessionId: string,
    options?: { frameAlreadyDelivered?: boolean },
  ) => Promise<void>;
};

type Subscription = {
  sessionId: string;
  listeners: Set<SessionListener>;
  lastSeq: number;
  lifecycle: AgentRuntimeLifecycle | null;
};

type AttachResult = {
  sessionId: string;
  status: "attached" | "resync-required" | "rejected";
  reason?: string;
};

export type HubSubscribeStatus = "idle" | "connected" | "reconnecting";

const subscriptions = new Map<string, Subscription>();

let connection: Promise<void> | null = null;
let controller: AbortController | null = null;
let wake: (() => void) | null = null;
let epoch = 0;

const SUBSCRIBER_ID_KEY = "flow-builder-agent-runtime-subscriber-id";
let fallbackSubscriberId: string | null = null;

let hubStatus: HubSubscribeStatus = "idle";
const hubStatusListeners = new Set<() => void>();

/** Three missed server heartbeats (15s apart) — the connection is dead, not idle. */
const STREAM_IDLE_TIMEOUT_MS = 45_000;

function getSubscriberId(): string {
  try {
    const stored = window.sessionStorage.getItem(SUBSCRIBER_ID_KEY);
    if (stored) return stored;
    const created = crypto.randomUUID();
    window.sessionStorage.setItem(SUBSCRIBER_ID_KEY, created);
    return created;
  } catch {
    fallbackSubscriberId ??= crypto.randomUUID();
    return fallbackSubscriberId;
  }
}

function setHubStatus(next: HubSubscribeStatus): void {
  if (hubStatus === next) return;
  hubStatus = next;
  for (const listener of [...hubStatusListeners]) {
    try {
      listener();
    } catch (error) {
      console.error("[agent-runtime] Hub status listener failed", error);
    }
  }
}

export function getHubSubscribeStatus(): HubSubscribeStatus {
  return hubStatus;
}

export function subscribeHubStatus(listener: () => void): () => void {
  hubStatusListeners.add(listener);
  return () => {
    hubStatusListeners.delete(listener);
  };
}

/** Wake the reconnect backoff early (FlowOps AgentChatView Retry now). */
export function retryAgentRuntimeNow(): void {
  wake?.();
}

function sessionsPayload() {
  return [...subscriptions.values()].map((sub) => ({
    sessionId: sub.sessionId,
    since: sub.lastSeq,
  }));
}

function emitLifecycle(
  sessionId: string,
  kind: "subscribed" | "lost" | "resync",
  message?: string,
  resyncOptions?: { frameAlreadyDelivered?: boolean },
): void {
  const sub = subscriptions.get(sessionId);
  if (!sub?.lifecycle) return;
  try {
    if (kind === "subscribed") sub.lifecycle.onSubscribed?.(sessionId);
    else if (kind === "lost") sub.lifecycle.onSubscribeLost?.(sessionId, message ?? "disconnected");
    else if (kind === "resync") void sub.lifecycle.onSubscribeResync?.(sessionId, resyncOptions);
  } catch (error) {
    console.error("[agent-runtime] Lifecycle hook failed", error);
  }
}

function handleAttachResults(results: unknown): void {
  if (!Array.isArray(results)) return;
  for (const entry of results as AttachResult[]) {
    const sessionId = entry?.sessionId?.trim();
    if (!sessionId) continue;
    const sub = subscriptions.get(sessionId);
    if (!sub) continue;
    if (entry.status === "resync-required") {
      sub.lastSeq = 0;
      // One path only: paint via frame; lifecycle schedules orphan resume (no second fetch/paint).
      for (const listener of [...sub.listeners]) {
        try {
          listener({ event: "resync-required", data: { sessionId } });
        } catch (error) {
          console.error("[agent-runtime] Resync listener failed", error);
        }
      }
      emitLifecycle(sessionId, "resync", undefined, { frameAlreadyDelivered: true });
    } else if (entry.status === "rejected") {
      emitLifecycle(sessionId, "lost", entry.reason ?? "Session unavailable");
      setHubStatus("reconnecting");
    }
  }
}

function deliverMx(payload: {
  sessionId?: string;
  seq?: number | null;
  event?: string;
  data?: unknown;
}): void {
  const sub = payload.sessionId ? subscriptions.get(payload.sessionId) : undefined;
  if (!sub || !payload.event) return;
  if (typeof payload.seq === "number" && payload.seq > sub.lastSeq) {
    sub.lastSeq = payload.seq;
  }
  const frame: SseFrame = { event: payload.event, data: payload.data };
  for (const listener of [...sub.listeners]) {
    try {
      listener(frame);
    } catch (error) {
      console.error("[agent-runtime] Frame listener failed", error);
    }
  }
}

function handleFrame(event: string, raw: string, onReady: () => void): boolean {
  if (raw === "[DONE]") return false;

  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    return true;
  }

  if (event === "mx") {
    deliverMx(payload as Parameters<typeof deliverMx>[0]);
  } else if (event === "ready") {
    onReady();
    handleAttachResults((payload as { sessions?: unknown })?.sessions);
    setHubStatus("connected");
    for (const sessionId of subscriptions.keys()) {
      emitLifecycle(sessionId, "subscribed");
    }
  }
  return true;
}

async function readWithIdleTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  let timer: number | undefined;
  try {
    return await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        timer = window.setTimeout(
          () => reject(new Error("Live updates timed out")),
          STREAM_IDLE_TIMEOUT_MS,
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) window.clearTimeout(timer);
  }
}

async function consume(
  response: Response,
  signal: AbortSignal,
  myEpoch: number,
  onReady: () => void,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Agent runtime stream returned an empty body");

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "message";

  while (!signal.aborted) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await readWithIdleTimeout(reader);
    } catch (error) {
      if (signal.aborted || (error as { name?: string })?.name === "AbortError") return;
      throw error;
    }
    if (chunk.done) return;
    if (epoch !== myEpoch) return;

    buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, "\n");
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
        continue;
      }
      if (!line.startsWith("data:")) continue;
      if (!handleFrame(currentEvent, line.slice(5).trimStart(), onReady)) return;
    }
  }
}

async function resyncAllSessions(): Promise<void> {
  await Promise.all(
    [...subscriptions.keys()].map(async (sessionId) => {
      const sub = subscriptions.get(sessionId);
      if (!sub?.lifecycle?.onSubscribeResync) return;
      try {
        // Reconnect path: no attach frame yet — resync must paint + plan resume.
        await sub.lifecycle.onSubscribeResync(sessionId, { frameAlreadyDelivered: false });
      } catch (error) {
        console.warn("[agent-runtime] Resync before reconnect failed", error);
      }
    }),
  );
}

function waitForRetry(ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(() => {
      wake = null;
      resolve();
    }, ms);
    wake = () => {
      window.clearTimeout(timer);
      wake = null;
      resolve();
    };
  });
}

function teardown(): void {
  controller?.abort();
  controller = null;
  wake = null;
  connection = null;
  epoch++;
  if (subscriptions.size === 0) setHubStatus("idle");
}

function startConnection(): Promise<void> {
  const myEpoch = ++epoch;
  const ctrl = new AbortController();
  controller = ctrl;
  const subscriberId = getSubscriberId();

  return (async () => {
    let backoffMs = 500;
    let lost = false;

    while (!ctrl.signal.aborted && subscriptions.size > 0) {
      const attempt = new AbortController();
      const abortAttempt = () => attempt.abort();
      ctrl.signal.addEventListener("abort", abortAttempt, { once: true });
      try {
        if (lost) {
          await resyncAllSessions();
          if (ctrl.signal.aborted) return;
        }

        const res = await flowopsFetch("/api/agent-runtime/stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscriberId, sessions: sessionsPayload() }),
          signal: attempt.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(
            (err as { error?: string }).error ?? `Agent runtime stream failed (${res.status})`,
          );
        }

        await consume(res, attempt.signal, myEpoch, () => {
          backoffMs = 500;
        });
        if (ctrl.signal.aborted || epoch !== myEpoch) return;
      } catch (error) {
        if (ctrl.signal.aborted) return;
        if ((error as { name?: string } | null)?.name === "AbortError") return;
        console.warn("[agent-runtime] Reconnecting stream", error);
        setHubStatus("reconnecting");
        const message = error instanceof Error ? error.message : String(error);
        for (const sessionId of subscriptions.keys()) {
          emitLifecycle(sessionId, "lost", message);
        }
      } finally {
        attempt.abort();
        ctrl.signal.removeEventListener("abort", abortAttempt);
      }
      // FlowOps: pace every disconnect (clean or error) — never hammer reconnect.
      lost = true;
      setHubStatus("reconnecting");
      await waitForRetry(backoffMs);
      backoffMs = Math.min(backoffMs * 2, 8_000);
    }
  })();
}

function ensureConnection(): void {
  connection ??= startConnection().finally(() => {
    connection = null;
  });
}

async function syncSubscription(
  subscribe: { sessionId: string; since: number }[],
  unsubscribe: string[],
): Promise<void> {
  const connectionAtCall = connection;
  try {
    const res = await flowopsFetch("/api/agent-runtime/stream/subscriptions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        subscriberId: getSubscriberId(),
        subscribe,
        unsubscribe,
      }),
    });
    if (res.status === 404) {
      if (subscriptions.size === 0) return;
      if (connection !== connectionAtCall) return;
      teardown();
      ensureConnection();
      return;
    }
    if (!res.ok) return;
    handleAttachResults(((await res.json()) as { sessions?: unknown }).sessions);
  } catch {
    // Hot-add is best-effort; the reconnect loop re-attaches with since.
  }
}

/**
 * Listen for product frames on a build session. Returns an unsubscribe that
 * removes only this listener (the hub stays up while other listeners remain).
 */
export function subscribeAgentRuntimeSession(
  sessionId: string,
  listener: SessionListener,
  lifecycle?: AgentRuntimeLifecycle,
): () => void {
  const id = sessionId.trim();
  if (!id) return () => undefined;

  let sub = subscriptions.get(id);
  if (!sub) {
    sub = { sessionId: id, listeners: new Set(), lastSeq: 0, lifecycle: lifecycle ?? null };
    subscriptions.set(id, sub);
    if (connection) {
      void syncSubscription([{ sessionId: id, since: 0 }], []);
    } else {
      ensureConnection();
    }
  } else if (lifecycle) {
    sub.lifecycle = lifecycle;
  }
  // Hub attach must be exclusive — orphaned listeners (HMR / Map desync) were
  // double-painting every tool-call / text-delta into DevTrace + transcript.
  if (lifecycle) {
    sub.listeners.clear();
  }
  sub.listeners.add(listener);

  return () => {
    const current = subscriptions.get(id);
    if (!current) return;
    current.listeners.delete(listener);
    if (current.listeners.size > 0) return;
    subscriptions.delete(id);
    if (subscriptions.size === 0) {
      teardown();
      return;
    }
    void syncSubscription([], [id]);
  };
}

export function resetAgentRuntimeStreamForTests(): void {
  subscriptions.clear();
  teardown();
  epoch = 0;
  hubStatus = "idle";
}
